/**
 * First-run onboarding. Full screen (no tab bar): language → role →
 * mom setup (expecting / baby here) or partner code entry → done.
 * A #join=CODE link preselects the partner path with the code filled in.
 */
import { useEffect, useState } from 'react';
import { useT } from '../i18n/i18n';
import { useActions, useApp } from '../lib/store';
import { nowIso, toLocalIso } from '../lib/time';
import { lbOzToGrams } from '../lib/units';
import { clearJoinHash, parseJoinCode } from '../lib/pairing-link';
import { Icons, Sheet } from '../components/ui';
import { PrivacyBody } from './PrivacyScreen';
import { PairCodeEntry } from '../components/PairingPanel';
import type { Locale, Profile } from '../lib/types';

type Step = 'language' | 'role' | 'mom' | 'partner' | 'connected';

const INPUT_CLS =
  'w-full rounded-2xl border border-line bg-surface px-4 py-3 text-ink';

export default function OnboardingScreen() {
  const t = useT();
  const { settings } = useApp();
  const { completeOnboarding, setSettings } = useActions();

  // A join link means this is almost certainly the partner's phone.
  const [prefillCode] = useState<string | null>(() => {
    const code = parseJoinCode();
    if (code) clearJoinHash();
    return code;
  });

  const [step, setStep] = useState<Step>('language');
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);

  // Mom setup state.
  const [babyStatus, setBabyStatus] = useState<'expecting' | 'here' | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [birthLocal, setBirthLocal] = useState(() => nowIso().slice(0, 16));
  const [nickname, setNickname] = useState('');
  const [lbStr, setLbStr] = useState('');
  const [ozStr, setOzStr] = useState('');
  const [gStr, setGStr] = useState('');

  // The "connected!" beat: pause, then finish partner onboarding.
  useEffect(() => {
    if (step !== 'connected') return;
    const id = setTimeout(() => void completeOnboarding('partner'), 1800);
    return () => clearTimeout(id);
  }, [step, completeOnboarding]);

  const chooseLanguage = async (locale: Locale) => {
    await setSettings({ locale });
    setStep(prefillCode ? 'partner' : 'role');
  };

  const birthWeightG = (): number | undefined => {
    if (settings.weightUnit === 'lboz') {
      const lb = parseFloat(lbStr);
      const oz = parseFloat(ozStr);
      if (Number.isNaN(lb) && Number.isNaN(oz)) return undefined;
      const grams = lbOzToGrams(Number.isNaN(lb) ? 0 : lb, Number.isNaN(oz) ? 0 : oz);
      return grams > 0 ? Math.round(grams) : undefined;
    }
    const grams = parseFloat(gStr);
    return !Number.isNaN(grams) && grams > 0 ? Math.round(grams) : undefined;
  };

  const finishMom = async () => {
    if (busy || !babyStatus) return;
    setBusy(true);
    try {
      const profile: Profile =
        babyStatus === 'expecting'
          ? { phase: 'prenatal', dueDate, updatedAt: nowIso() }
          : {
              phase: 'baby',
              birthAt: toLocalIso(new Date(birthLocal)),
              babyNickname: nickname.trim() || undefined,
              birthWeightG: birthWeightG(),
              updatedAt: nowIso(),
            };
      await completeOnboarding('mom', profile);
    } finally {
      setBusy(false);
    }
  };

  const momReady =
    babyStatus === 'expecting' ? dueDate !== '' : babyStatus === 'here' ? birthLocal !== '' : false;

  const back = () => {
    if (step === 'role') setStep('language');
    else if (step === 'mom' || step === 'partner') setStep('role');
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col px-6 pb-8 pt-6">
      {/* Back button on the middle steps */}
      <div className="h-10">
        {step === 'role' || step === 'mom' || step === 'partner' ? (
          <button
            type="button"
            onClick={back}
            className="-ml-3 flex min-h-[44px] items-center gap-1 rounded-full px-3 font-bold text-ink-soft"
          >
            {Icons.chevronRight('h-5 w-5 rotate-180')}
            {t.common.actions.back}
          </button>
        ) : null}
      </div>

      {/* Brand header */}
      <div className="mb-6 text-center">
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-3xl bg-primary-soft text-primary">
          {Icons.heart('h-8 w-8')}
        </div>
        <h1 className="text-2xl">{t.common.appName}</h1>
        <p className="mt-1 text-sm text-ink-soft">{t.common.practiceTagline}</p>
      </div>

      <div className="flex-1">
        {step === 'language' ? (
          <div className="animate-float-up space-y-4">
            <p className="text-center text-ink-soft">{t.onboarding.welcome.body}</p>
            <h2 className="pt-2 text-center text-lg">{t.onboarding.welcome.chooseLanguage}</h2>
            <button
              type="button"
              onClick={() => void chooseLanguage('en')}
              className="card block w-full px-5 py-5 text-center text-xl font-bold text-ink"
            >
              {t.onboarding.welcome.english}
            </button>
            <button
              type="button"
              onClick={() => void chooseLanguage('es')}
              className="card block w-full px-5 py-5 text-center text-xl font-bold text-ink"
            >
              {t.onboarding.welcome.spanish}
            </button>
          </div>
        ) : null}

        {step === 'role' ? (
          <div className="animate-float-up space-y-4">
            <h2 className="text-center text-lg">{t.onboarding.role.title}</h2>
            <button
              type="button"
              onClick={() => setStep('mom')}
              className="card flex w-full items-center justify-between gap-3 px-5 py-5 text-left"
            >
              <span>
                <span className="block text-lg font-bold text-ink">{t.onboarding.role.momTitle}</span>
                <span className="mt-0.5 block text-sm text-ink-soft">{t.onboarding.role.momHint}</span>
              </span>
              <span className="shrink-0 text-ink-faint">{Icons.chevronRight('h-5 w-5')}</span>
            </button>
            <button
              type="button"
              onClick={() => setStep('partner')}
              className={`card flex w-full items-center justify-between gap-3 px-5 py-5 text-left ${
                prefillCode ? 'border-line bg-primary-soft' : ''
              }`}
            >
              <span>
                <span className="block text-lg font-bold text-ink">{t.onboarding.role.partnerTitle}</span>
                <span className="mt-0.5 block text-sm text-ink-soft">{t.onboarding.role.partnerHint}</span>
                {prefillCode ? (
                  <span className="mt-1 block text-sm font-bold text-primary">
                    {t.onboarding.role.inviteDetected}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 text-ink-faint">{Icons.chevronRight('h-5 w-5')}</span>
            </button>
          </div>
        ) : null}

        {step === 'mom' ? (
          <div className="animate-float-up space-y-4">
            <h2 className="text-center text-lg">{t.onboarding.baby.title}</h2>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setBabyStatus('expecting')}
                aria-pressed={babyStatus === 'expecting'}
                className={`min-h-[56px] flex-1 rounded-2xl border px-3 py-3 font-bold ${
                  babyStatus === 'expecting'
                    ? 'border-line bg-primary text-on-primary'
                    : 'border-line bg-surface text-ink'
                }`}
              >
                {t.onboarding.baby.expecting}
              </button>
              <button
                type="button"
                onClick={() => setBabyStatus('here')}
                aria-pressed={babyStatus === 'here'}
                className={`min-h-[56px] flex-1 rounded-2xl border px-3 py-3 font-bold ${
                  babyStatus === 'here'
                    ? 'border-line bg-primary text-on-primary'
                    : 'border-line bg-surface text-ink'
                }`}
              >
                {t.onboarding.baby.here}
              </button>
            </div>

            {babyStatus === 'expecting' ? (
              <div className="animate-float-up space-y-2">
                <label className="block text-sm font-bold text-ink-soft" htmlFor="ob-due-date">
                  {t.onboarding.baby.dueDateLabel}
                </label>
                <input
                  id="ob-due-date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className={INPUT_CLS}
                />
                <p className="text-sm text-ink-soft">{t.onboarding.baby.dueDateHint}</p>
              </div>
            ) : null}

            {babyStatus === 'here' ? (
              <div className="animate-float-up space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-bold text-ink-soft" htmlFor="ob-birth">
                    {t.onboarding.baby.birthDateLabel}
                  </label>
                  <input
                    id="ob-birth"
                    type="datetime-local"
                    value={birthLocal}
                    max={nowIso().slice(0, 16)}
                    onChange={(e) => setBirthLocal(e.target.value)}
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-bold text-ink-soft" htmlFor="ob-nickname">
                    {t.onboarding.baby.nicknameLabel}
                  </label>
                  <input
                    id="ob-nickname"
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder={t.settings.baby.nicknamePlaceholder}
                    className={INPUT_CLS}
                  />
                  <p className="mt-1 text-sm text-ink-soft">{t.onboarding.baby.nicknameHint}</p>
                </div>
                <div>
                  <span className="mb-1 block text-sm font-bold text-ink-soft">
                    {t.onboarding.baby.weightLabel}
                  </span>
                  {settings.weightUnit === 'lboz' ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        value={lbStr}
                        onChange={(e) => setLbStr(e.target.value)}
                        aria-label={t.settings.units.lb}
                        className={INPUT_CLS}
                      />
                      <span className="text-sm font-bold text-ink-soft">{t.settings.units.lb}</span>
                      <input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="15"
                        value={ozStr}
                        onChange={(e) => setOzStr(e.target.value)}
                        aria-label={t.settings.units.oz}
                        className={INPUT_CLS}
                      />
                      <span className="text-sm font-bold text-ink-soft">{t.settings.units.oz}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={gStr}
                        onChange={(e) => setGStr(e.target.value)}
                        aria-label={t.settings.units.g}
                        className={INPUT_CLS}
                      />
                      <span className="text-sm font-bold text-ink-soft">{t.settings.units.g}</span>
                    </div>
                  )}
                  <p className="mt-1 text-sm text-ink-soft">{t.onboarding.baby.weightHint}</p>
                </div>
              </div>
            ) : null}

            {babyStatus ? (
              <button
                type="button"
                disabled={!momReady || busy}
                onClick={() => void finishMom()}
                className="btn-primary min-h-[52px] w-full py-3.5 text-lg disabled:opacity-50"
              >
                {t.onboarding.baby.finish}
              </button>
            ) : null}
          </div>
        ) : null}

        {step === 'partner' ? (
          <div className="animate-float-up space-y-4">
            <h2 className="text-center text-lg">{t.onboarding.partner.title}</h2>
            <p className="text-sm text-ink-soft">{t.onboarding.partner.body}</p>
            <PairCodeEntry initialCode={prefillCode} onJoined={() => setStep('connected')} />
          </div>
        ) : null}

        {step === 'connected' ? (
          <div className="animate-float-up space-y-4 pt-6 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-good-soft text-good">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-10 w-10"
                aria-hidden="true"
              >
                <path d="M4.5 12.5 10 18 19.5 7" />
              </svg>
            </div>
            <h2 className="text-2xl">{t.onboarding.partner.connectedTitle}</h2>
            <p className="text-ink-soft">{t.onboarding.partner.connectedBody}</p>
          </div>
        ) : null}
      </div>

      {/* Privacy + disclaimer footer */}
      <div className="mt-8 space-y-2">
        <p className="text-xs text-ink-faint">
          {t.onboarding.privacyNote}{' '}
          <button
            type="button"
            onClick={() => setShowPrivacy(true)}
            className="underline decoration-dotted underline-offset-2"
          >
            {t.settings.about.privacy}
          </button>
        </p>
        <p className="text-xs text-ink-faint">{t.common.disclaimer}</p>
      </div>

      <Sheet open={showPrivacy} onClose={() => setShowPrivacy(false)} title={t.privacy.title}>
        <PrivacyBody />
      </Sheet>
    </div>
  );
}
